import { db, auth } from "./firebase-config";
import { signInAnonymously } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";

// Ensure user is authenticated (anonymously) before accessing Firestore
const ensureAuth = async (): Promise<void> => {
  // Skip authentication for now - Firebase rules should be set to allow public access
  console.log('🔓 Skipping Firebase authentication - ensure Firestore rules allow public access');
  return;
  
  /* To enable authentication later:
  if (!auth.currentUser) {
    console.log('🔐 No Firebase user detected, signing in anonymously...');
    try {
      await signInAnonymously(auth);
      console.log('✅ Anonymous Firebase authentication successful');
    } catch (error) {
      console.error('❌ Firebase anonymous auth failed:', error);
      throw error;
    }
  }
  */
};

export interface Candidate {
  id?: string;
  name: string;
  email: string;
  phone: string;
  jobTitle?: string;
  company?: string;
  salary?: number;
  stage: string;
  countryApplyingTo?: string;
  passportNumber?: string;
  city?: string;
  country?: string;
  notes?: string;
  source?: string;
  riskLevel?: 'low' | 'medium' | 'high';
  confidence?: number;
  detectedLanguage?: string;
  photoLink?: string;
  cvLink?: string;
  aiNotes?: string;
  createdAt?: any;
  updatedAt?: any;
  // Soft delete fields
  isDeleted?: boolean;
  deletedAt?: any;
  // Extended fields for compatibility
  _id?: string;
  fullNameArabic?: string;
  address?: string;
  nationality?: string;
  religion?: string;
  dateOfBirth?: string;
  placeOfBirth?: string;
  father?: string;
  mother?: string;
  age?: string;
  height?: string;
  weight?: string;
  civilStatus?: string;
  spouse?: string;
  numberOfKids?: string;
  educationLevel?: string;
  englishLevel?: string;
  arabicLevel?: string;
  workPosition?: string;
  workCountry?: string;
  workYears?: string;
  contact?: string;
  emergencyContact?: string;
  workCompany?: string;
  workCity?: string;
  dateIssued?: string;
  dateExpiry?: string;
  placeIssued?: string;
  skills?: string[];
  remarks?: string;
  profilePhoto?: string;
  fullBodyPhoto?: string;
  passportCopy?: string;
  certificates?: string[];
  departureDate?: string;
  departureTime?: string;
}

export interface Job {
  id?: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  salaryKES?: number;
  salaryRange?: {
    min: number;
    max: number;
    currency: string;
  };
  status: string;
  applicants?: number;
  description?: string;
  requirements?: string | string[];
  benefits?: string | string[];
  workingHours?: string;
  contactPerson?: string;
  phone?: string;
  postedDate?: string;
  // Extended fields from Job Generator
  gender?: string[];
  qualifications?: string[];
  locations?: { country: string; city: string }[];
  positions?: string[];
  experience?: string;
  accommodation?: string;
  additionalDetails?: string;
  type?: 'full-time' | 'part-time' | 'contract';
  city?: string;
  country?: string;
  vacancies?: number;
  uploads?: string[];
  ageMaximumRange?: string;
  nationality?: string;
  dutyHours?: string;
  workDays?: string;
  overtime?: string;
  transportProvision?: string;
  contractPeriod?: string;
  createdAt?: any;
  updatedAt?: any;
}

export interface Task {
  id?: string;
  _id?: string;
  title: string;
  description: string;
  assignedTo: string;
  assignedBy: string;
  dueDate: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE';
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  category: string;
  candidateId?: string;
  jobId?: string;
  createdAt?: any;
  updatedAt?: any;
}

export interface Notification {
  id?: string;
  title: string;
  message: string;
  type: 'task_overdue' | 'appointment_today' | 'candidate_stage_change' | 'role_expiration' | 'general';
  read: boolean;
  targetUserId?: string;
  relatedId?: string; // ID of the related task, appointment, candidate, etc.
  createdAt?: any;
  updatedAt?: any;
}

export interface Appointment {
  id?: string;
  _id?: string;
  candidateName: string;
  clientName: string;
  date: string;
  time: string;
  type: 'Interview' | 'Medical' | 'Document Review' | 'Orientation' | 'Follow-up';
  status: 'Scheduled' | 'Confirmed' | 'Completed' | 'Cancelled' | 'Rescheduled';
  location?: string;
  notes?: string;
  candidateId?: string;
  interviewer?: string;
  scheduledAt?: any;
  createdAt?: any;
  updatedAt?: any;
}

// CANDIDATE OPERATIONS
export const addCandidate = async (data: Omit<Candidate, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const candidateData = {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, "candidates"), candidateData);
  return docRef.id;
};

export const getCandidates = async (filters?: {
  stage?: string;
  source?: string;
  riskLevel?: string;
  search?: string;
  limit?: number;
  startAfterDoc?: DocumentSnapshot;
}): Promise<{ candidates: Candidate[], lastDoc?: DocumentSnapshot }> => {
  let q = query(collection(db, "candidates"));

  // Apply filters
  if (filters?.stage) {
    q = query(q, where("stage", "==", filters.stage));
  }
  if (filters?.source) {
    q = query(q, where("source", "==", filters.source));
  }
  if (filters?.riskLevel) {
    q = query(q, where("riskLevel", "==", filters.riskLevel));
  }

  // Add ordering
  q = query(q, orderBy("createdAt", "desc"));

  // Add pagination
  if (filters?.limit) {
    q = query(q, limit(filters.limit));
  }
  if (filters?.startAfterDoc) {
    q = query(q, startAfter(filters.startAfterDoc));
  }

  const snapshot = await getDocs(q);
  const candidates = snapshot.docs.map(doc => ({ 
    id: doc.id, 
    ...doc.data() 
  } as Candidate));

  const lastDoc = snapshot.docs[snapshot.docs.length - 1];

  return { candidates, lastDoc };
};

export const getCandidateById = async (id: string): Promise<Candidate | null> => {
  const docRef = doc(db, "candidates", id);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as Candidate;
  }
  return null;
};

export const updateCandidate = async (id: string, updates: Partial<Candidate>): Promise<void> => {
  const docRef = doc(db, "candidates", id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteCandidate = async (id: string): Promise<void> => {
  const docRef = doc(db, "candidates", id);
  await deleteDoc(docRef);
};

export const subscribeToCandidates = (
  callback: (candidates: Candidate[]) => void,
  filters?: { stage?: string; source?: string; riskLevel?: string }
): Unsubscribe => {
  let q = query(collection(db, "candidates"));
  
  // Apply filters
  if (filters?.stage) {
    q = query(q, where("stage", "==", filters.stage));
  }
  if (filters?.source) {
    q = query(q, where("source", "==", filters.source));
  }
  if (filters?.riskLevel) {
    q = query(q, where("riskLevel", "==", filters.riskLevel));
  }

  q = query(q, orderBy("createdAt", "desc"));

  return onSnapshot(q, (snapshot) => {
    const candidates = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Candidate));
    callback(candidates);
  });
};

// JOB OPERATIONS
export const addJob = async (data: Omit<Job, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const jobData = {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, "jobs"), jobData);
  return docRef.id;
};

export const getJobs = async (): Promise<Job[]> => {
  const q = query(collection(db, "jobs"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Job));
};

export const updateJob = async (id: string, updates: Partial<Job>): Promise<void> => {
  const docRef = doc(db, "jobs", id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteJob = async (id: string): Promise<void> => {
  const docRef = doc(db, "jobs", id);
  await deleteDoc(docRef);
};

// TASK OPERATIONS
export const addTask = async (data: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  await ensureAuth();
  const taskData = {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, "tasks"), taskData);
  console.log('✅ Task added to Firebase:', docRef.id);
  return docRef.id;
};

export const getTasks = async (): Promise<Task[]> => {
  await ensureAuth();
  const q = query(collection(db, "tasks"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  const tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task));
  console.log('📋 Retrieved tasks from Firebase:', tasks.length);
  return tasks;
};

export const updateTask = async (id: string, updates: Partial<Task>): Promise<void> => {
  await ensureAuth();
  const docRef = doc(db, "tasks", id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
  console.log('✅ Task updated in Firebase:', id);
};

export const deleteTask = async (id: string): Promise<void> => {
  await ensureAuth();
  const docRef = doc(db, "tasks", id);
  await deleteDoc(docRef);
  console.log('🗑️ Task deleted from Firebase:', id);
};

// APPOINTMENT OPERATIONS
export const addAppointment = async (data: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const appointmentData = {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, "appointments"), appointmentData);
  return docRef.id;
};

export const getAppointments = async (): Promise<Appointment[]> => {
  const q = query(collection(db, "appointments"), orderBy("scheduledAt", "asc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Appointment));
};

export const updateAppointment = async (id: string, updates: Partial<Appointment>): Promise<void> => {
  const docRef = doc(db, "appointments", id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteAppointment = async (id: string): Promise<void> => {
  const docRef = doc(db, "appointments", id);
  await deleteDoc(docRef);
};

// NOTIFICATION OPERATIONS
export const addNotification = async (data: Omit<Notification, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const notificationData = {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, "notifications"), notificationData);
  return docRef.id;
};

export const getNotifications = async (userId?: string): Promise<Notification[]> => {
  let q = query(collection(db, "notifications"));
  
  if (userId) {
    q = query(q, where("targetUserId", "==", userId));
  }
  
  q = query(q, orderBy("createdAt", "desc"));
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Notification));
};

export const updateNotification = async (id: string, updates: Partial<Notification>): Promise<void> => {
  const docRef = doc(db, "notifications", id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteNotification = async (id: string): Promise<void> => {
  const docRef = doc(db, "notifications", id);
  await deleteDoc(docRef);
};